import { useRouter } from "next/router";
import React from "react";
import Head from "next/head";
import Image from "next/image";
import Link from "next/link";
import { BsArrowRight } from "react-icons/bs";
import {
  BASE_ASSETS_URL,
  BASE_URL,
  BASE_URL_POST,
} from "../../../constants/constants";
import { FiSearch } from "react-icons/fi";
import { ThreeDots } from "react-loader-spinner";
import StickyLoginCard from "components/StickyLoginCard";
import { CLMLinkPrimaryArrow } from "components/CLM/v3/Buttons";

// remove hyphen from url and capitalize first letter of each word
const removeHyphenAndCapitalize = (str) => {
  return str
    .replace(/-/g, " ")
    .replace(/\w\S*/g, (w) => w.replace(/^\w/, (c) => c.toUpperCase()));
};

const addHyphenInUrl = (str) => {
  return str.replace(/\s+/g, "-").toLowerCase();
};

export async function getServerSideProps(context) {
  const { category } = context.query;
  const response = await fetch(`${BASE_URL}contracts-by-category/${category}`);
  const data = await response.json();
  let { contracts } = data;
  // console.log(contracts);
  let groupBySubCategory = {};

  contracts?.forEach((contract) => {
    if (!groupBySubCategory[contract.subcategories]) {
      groupBySubCategory[contract.subcategories] = [];
    } else {
      groupBySubCategory[contract.subcategories].push(contract);
    }
  });

  return {
    props: {
      title: "",
      description: "",
      groupedContracts: groupBySubCategory,
    },
  };
}

const Category = ({ title, description, groupedContracts }) => {
  const router = useRouter();
  const { category } = router.query;

  const [search, setSearch] = React.useState("");
  const [searchLoading, setSearchLoading] = React.useState(false);
  const [searchResults, setSearchResults] = React.useState([]);

  const [activeSection, setActiveSection] = React.useState("");

  const handleScroll = () => {
    const scrollPosition = window.scrollY;

    const activeSection = Object.keys(groupedContracts).find((subCategory) => {
      const sectionElement = document.getElementById(
        addHyphenInUrl(subCategory)
      );
      if (sectionElement) {
        const sectionTop = sectionElement.offsetTop;
        const sectionBottom = sectionTop + sectionElement.offsetHeight;
        return scrollPosition >= sectionTop && scrollPosition < sectionBottom;
      }
      return false;
    });

    setActiveSection(activeSection || "");
  };

  React.useEffect(() => {
    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [category]);

  React.useEffect(() => {
    const handleSearch = async () => {
      // console.log("searching");
      setSearchLoading(true);
      const response = await fetch(`${BASE_URL_POST}search-contracts`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ searchQuery: search }),
      });

      const data = await response.json();
      const { contracts } = data;

      setSearchResults(contracts);
      setSearchLoading(false);
    };

    let timer = setTimeout(() => {
      if (search.length > 0) {
        handleSearch();
      } else {
        setSearchResults([]);
      }
    }, 1000);

    return () => {
      clearTimeout(timer);
    };
  }, [search]);

  const bgColors = [
    "bg-green-100/50",
    "bg-yellow-100/50",
    "bg-red-100/50",
    "bg-gray-100/50",
    "bg-blue-100/50",
    "bg-purple-100/50",
    "bg-indigo-100/50",
  ];

  return (
    <div className="md:mt-20 mt-8">
      <Head>
        <meta charSet="utf-8" />
        <title>
          Download {removeHyphenAndCapitalize(category)} Documents | BoloSign
        </title>

        <meta
          name="description"
          content={`Get all the Legal documents for ${removeHyphenAndCapitalize(
            category
          )}, use them as you like, download, modify and send for signing using BoloSign`}
        />

        <meta name="geo.region" content="IN" />
        <meta name="geo.position" content="22.351115;78.667743" />
        <meta name="ICBM" content="22.351115, 78.667743" />
        <meta name="author" content="BoloSign" />
        <meta name="publisher" content="BoloSign" />
        <meta name="keywords" content=" " />
        <meta
          property="og:url"
          content={`https://www.bolosign.com/signature/contracts/${category}/`}
        />
        <meta property="og:type" content="website" />
        <meta
          property="og:title"
          content={`Get all the Legal documents for ${removeHyphenAndCapitalize(
            category
          )}, use them as you like, download, modify and send for signing using BoloForms`}
        />
        <meta name="twitter:site" content="@boloforms" />
        <meta
          property="twitter:url"
          content={`https://www.bolosign.com/signature/contracts/${category}/`}
        />
        <meta
          name="twitter:title"
          content={`Get all the Legal documents for ${removeHyphenAndCapitalize(
            category
          )}, use them as you like, download, modify and send for signing using BoloForms`}
        />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:creator" content="@boloforms" />
        <meta property="twitter:domain" content="https://bolosign.com" />
        <meta name="twitter:description" content="" />
        <meta property="og:description" content="" />
        <meta
          name="robots"
          content="INDEX, FOLLOW, MAX-IMAGE-PREVIEW:LARGE, MAX-SNIPPET:-1, MAX-VIDEO-PREVIEW:-1"
        />
        <link
          rel="canonical"
          href={`https://www.bolosign.com/signature/contracts/${category}/`}
        />
      </Head>
      {/* {console.log(groupedContracts)} */}
      <div className="w-full max-w-screen-xl mx-auto flex flex-col scroll-smooth px-4 md:px-8 ">
        {/* heading section */}
        <div className="mx-auto w-full">
          <div className="text-3xl lg:text-5xl lg:leading-[55px] font-bold text-gray-800 text-center">
            {category && removeHyphenAndCapitalize(category)}
          </div>
          {/* search bar  */}
          <div className="max-w-screen-md mx-auto flex flex-col mt-8">
            <div className="flex justify-between items-center md:gap-2 w-full h-[50px] sm:h-[60px] p-2 rounded-lg bg-white border-[2px] border-gray-800">
              <div className="text-gray-800 text-3xl pl-3">
                <FiSearch />
              </div>
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search for a document"
                className="h-full w-full px-3 md:flex-1 text-xl text-gray-800 placeholder-gray-800 outline-none border-none"
                name=""
                id=""
              />
              {searchLoading && (
                <div className="text-gray-400">
                  <ThreeDots
                    height="40"
                    width="50"
                    radius="9"
                    color="#a070f2"
                    ariaLabel="three-dots-loading"
                    wrapperStyle={{}}
                    wrapperClassName=""
                    visible={true}
                  />
                </div>
              )}
            </div>
            {/* search results */}
            {searchResults.length > 0 && (
              <div className="border-2 rounded-lg bg-white mx-auto w-full mt-5 max-h-[480px] overflow-y-auto scrollbar-thin scrollbar-thumb-gray-400 scrollbar-corner-white scrollbar-track-transparent drop-shadow-xl ">
                {searchResults.map((contract, index) => (
                  <Link
                    key={index}
                    href={`/signature/contracts/${addHyphenInUrl(
                      contract?.categories
                    )}/${contract?.slug}`}
                    className="w-full group flex flex-col hover:bg-gray-50"
                  >
                    <div className="w-full text-left md:px-6 md:pt-6 px-4 pt-4">
                      <h2 className="text-gray-700 text-xl font-semibold leading-snug">
                        {contract?.name}
                      </h2>
                      <p className="font-normal text-base text-gray-700">
                        {contract?.description}
                      </p>
                    </div>
                    <div className="w-full flex justify-end items-center  pr-4 border-b border-gray-200 pb-4">
                      <CLMLinkPrimaryArrow
                        buttonText={"Create Document"}
                        buttonUrl={`/signature/contracts/${addHyphenInUrl(
                          contract?.categories
                        )}/${contract?.slug}`}
                      />
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
          {/* paragraph  */}
          <div
            className={`text-gray-800 text-md sm:text-lg max-w-screen-lg mx-auto text-center mt-6 sm:mt-8 ${
              searchResults.length > 0 ? "hidden" : "visible"
            }`}
          >
            {`Whether you're starting a business, building an empire, or have
            business-related administration to carry out, we're here to help with
            our range of professional business contracts. From LLC agreements to
            NDAs, we've got the legal documents you need to make your day-to-day
            easier. Explore our business legal documents, forms, and contracts
            below.`}
          </div>
        </div>
        {/* <div className="flex justify-between flex-col-reverse md:flex-row items-center gap-[50px]">
          <div className="flex flex-col gap-[30px] md:w-[50%]">
            <div className="text-3xl lg:text-5xl lg:leading-[55px] font-bold text-gray-800">
              {category && removeHyphenAndCapitalize(category)}
            </div>

            <div className="text-[18px] text-gray-600">
              Make Your Legal Document in Minutes.
            </div>
          </div>
          <div className="mx-auto w-full sm:w-[85%] md:w-[50%]">
            <Image
              src={`${BASE_ASSETS_URL}/contracts/contracts-image.png`}
              width="200"
              height="200"
              className="mx-auto"
              alt=""
            />
          </div>
        </div> */}
        {/* paragraph section */}
        {/* <div className="text-gray-600 text-lg">
          {`Whether you're starting a business, building an empire, or have
          business-related administration to carry out, we're here to help with
          our range of professional business contracts. From LLC agreements to
          NDAs, we've got the legal documents you need to make your day-to-day
          easier. Explore our business legal documents, forms, and contracts
          below.`}
        </div> */}
        {/* main section */}
        <div className="w-full flex justify-between flex-col lg:flex-row gap-12 xl:gap-20">
          {/* left navbar */}
          {/* <div className="self-center lg:self-start lg:sticky lg:top-[140px] flex justify-center">
            <StickyLoginCard pageProduct="" />
          </div> */}
          <div className="w-[250px] h-fit border-l-2 border-l-gray-700 lg:sticky top-[120px] lg:mt-[112px]">
            {Object.keys(groupedContracts).map((subCategory, index) => (
              <Link
                key={index}
                href={`#${addHyphenInUrl(subCategory)}`}
                // id={removeHyphenAndCapitalize(subCategory)}
                className={`text-md font-semibold py-4 px-4 block ${
                  activeSection === subCategory
                    ? "bg-black text-white"
                    : "text-black hover:text-violet-500"
                }`}
                onClick={(e) => {
                  e.preventDefault();
                  document
                    .querySelector(
                      `#${addHyphenInUrl(subCategory)?.replace(/&/g, "\\&")}`
                    )
                    .scrollIntoView({
                      behavior: "smooth",
                    });
                }}
              >
                {subCategory}
              </Link>
            ))}
          </div>
          {/* right section */}
          <div className="flex flex-col gap-10 flex-1">
            {/* search bar  */}
            {/* <div className="flex flex-col">
              <div className="flex justify-between items-center md:gap-2 w-full h-[60px] p-2 rounded-lg bg-white border">
                <div className="text-gray-400 text-3xl pl-3">
                  <FiSearch />
                </div>
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search for a document"
                  className="h-full w-full px-3 md:flex-1 text-xl text-gray-400 outline-none border-none"
                  name=""
                  id=""
                />
                {searchLoading && (
                  <div className="text-gray-400">
                    <ThreeDots
                      height="40"
                      width="50"
                      radius="9"
                      color="#a070f2"
                      ariaLabel="three-dots-loading"
                      wrapperStyle={{}}
                      wrapperClassName=""
                      visible={true}
                    />
                  </div>
                )}
              </div>
              {searchResults.length > 0 && (
                <div className="border-2 rounded-xl bg-white mx-auto w-full mt-5 max-h-[480px] overflow-y-auto drop-shadow-xl">
                  {searchResults.map((contract, index) => (
                    <div
                      key={index}
                      className="w-full flex flex-col md:flex-row justify-between gap-5 md:gap-10 p-5 md:p-10 border-t bg-white"
                    >
                      <div className="flex-1">                        
                        <Link
                          href={`/signature/contracts/${addHyphenInUrl(
                            contract?.categories
                          )}/${contract?.slug}`}
                          className="text-gray-700 text-xl font-bold hover:text-violet-600"
                        >
                          {contract?.name}
                        </Link>
                        <h3 className="font-normal text-md text-gray-500">
                          {contract?.description}
                        </h3>
                      </div>
                      <Link
                        href={`/signature/contracts/${addHyphenInUrl(
                          contract?.categories
                        )}/${contract?.slug}`}
                        className="flex items-center gap-4 text-violet-600"
                      >
                        Create Document{" "}
                        <BsArrowRight className="w-10 h-10 text-violet-700" />
                      </Link>
                    </div>
                  ))}
                </div>
              )}
            </div> */}
            {/* Contracts section */}
            {groupedContracts &&
              Object.keys(groupedContracts).map((subCategory, index) => (
                <div
                  key={index}
                  id={addHyphenInUrl(subCategory)}
                  className="flex flex-col gap-10 pt-[112px]"
                >
                  <div className="flex flex-col gap-6">
                    <h1 className="text-3xl md:text-4xl px-2 mt-4 font-semibold bg-white text-black rounded-t-xl">
                      {subCategory}
                    </h1>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2 gap-4">
                      {groupedContracts[subCategory].map((contract, index) => (
                        <Link
                          href={`/signature/contracts/${category}/${contract?.slug}`}
                          key={index}
                          className={`w-full group flex flex-col hover:bg-surface-light bg-surface md:p-6 p-4 border border-gray-200 transition-all duration-500 ease-in-out md:rounded-2xl rounded-xl gap-4`}
                        >
                          <div className="flex-1 flex flex-col gap-2">
                            <h1 className="text-gray-800 text-xl font-bold">
                              {contract?.name}
                            </h1>
                            <h3 className="font-normal text-md text-gray-800">
                              {contract?.description}
                            </h3>
                          </div>
                          <div className="w-full flex justify-start items-center  pr-4 ">
                            <CLMLinkPrimaryArrow
                              buttonText={"Create Document"}
                              buttonUrl={`/signature/contracts/${category}/${contract?.slug}`}
                            />
                          </div>
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Category;
